import { apiClient } from '@/lib/api-client';
import type { ApiSuccess } from '@/lib/api-client';
import type {
  CorrectionListQuery,
  CorrectionRequestDto,
  CorrectionTargetOption,
  CorrectionTypeSpec,
  CreateCorrectionRequest,
} from './types';

/**
 * Correction Request API.
 *
 * Endpoints (SPEC v1.0 §17.2 — see correction_requests/routes.rs):
 *   GET  /api/correction-requests            list (filter: status, monthly_batch_id)
 *   GET  /api/correction-requests/schema     typed correctable-entity catalog (spec-13 §8.8)
 *   GET  /api/correction-requests/targets    authorized, batch-scoped target picker options
 *   POST /api/correction-requests            create (in 'requested')
 *   GET  /api/correction-requests/:id        get one
 *   POST /api/correction-requests/:id/approve
 *   POST /api/correction-requests/:id/reject
 *   POST /api/correction-requests/:id/apply
 *
 * Read + create require `correction.request`; approve/reject/apply are routed
 * backend-side by target entity (`estimation.final_decide` for financial
 * entities, `correction.approve` for ordinary correction targets).
 */

/** GET the correction requests, optionally filtered by status / batch. */
export const fetchCorrectionRequests = async (
  query: CorrectionListQuery = {},
): Promise<CorrectionRequestDto[]> => {
  const res = await apiClient.get<ApiSuccess<CorrectionRequestDto[]>>('/correction-requests', {
    params: query,
  });
  return res.data.data ?? [];
};

/** POST create a new correction request (lands in 'requested'). */
export const createCorrectionRequest = async (
  body: CreateCorrectionRequest,
): Promise<CorrectionRequestDto> => {
  const res = await apiClient.post<ApiSuccess<CorrectionRequestDto>>('/correction-requests', body);
  return res.data.data;
};

export const approveCorrectionRequest = async (id: string): Promise<CorrectionRequestDto> => {
  const res = await apiClient.post<ApiSuccess<CorrectionRequestDto>>(
    `/correction-requests/${id}/approve`,
  );
  return res.data.data;
};

export const rejectCorrectionRequest = async (id: string): Promise<CorrectionRequestDto> => {
  const res = await apiClient.post<ApiSuccess<CorrectionRequestDto>>(
    `/correction-requests/${id}/reject`,
  );
  return res.data.data;
};

export const applyCorrectionRequest = async (id: string): Promise<CorrectionRequestDto> => {
  const res = await apiClient.post<ApiSuccess<CorrectionRequestDto>>(
    `/correction-requests/${id}/apply`,
  );
  return res.data.data;
};

/** GET the typed correctable-entity catalog driving the structured field editor. */
export const fetchCorrectionSchema = async (): Promise<CorrectionTypeSpec[]> => {
  const res = await apiClient.get<ApiSuccess<CorrectionTypeSpec[]>>('/correction-requests/schema');
  return res.data.data ?? [];
};

/** GET authorized, batch-scoped target options for one entity + monthly batch. */
export const fetchCorrectionTargets = async (
  entity: string,
  monthlyBatchId: string,
): Promise<CorrectionTargetOption[]> => {
  const res = await apiClient.get<ApiSuccess<CorrectionTargetOption[]>>(
    '/correction-requests/targets',
    { params: { entity, monthly_batch_id: monthlyBatchId } },
  );
  return res.data.data ?? [];
};
